import { IconAccountL } from 'icons/components/IconAccountL'
import { IconBagL } from 'icons/components/IconBagL'
import { IconFavoffS } from 'icons/components/IconFavoffS'
import type { IconProps } from 'icons/types/Icon.types'
import type { IconName } from 'icons/types/IconName.types'
import type { KeyboardEvent, MouseEvent } from 'react'
import { Keyboard } from 'types/keyboard'

import styles from './OptionItem.module.scss'
import texts from 'fukku/styles/texts.module.scss'

type allowedIcons = 'bag-l' | 'favoff-s' | 'account-l'
export type IconOptionItems = Extract<IconName, allowedIcons>

// Imports only used icons. If you need a new icon, add it to the allowedIcons type
// and add it to the availableIcons object.
function getIconComponent(
	icon: IconOptionItems
): React.ComponentType<IconProps> {
	const availableIcons: Record<
		IconOptionItems,
		React.ComponentType<IconProps>
	> = {
		'bag-l': IconBagL,
		'favoff-s': IconFavoffS,
		'account-l': IconAccountL,
	}
	return availableIcons[icon]
}

export interface OptionItemsType {
	readonly onChange: (
		event: MouseEvent<HTMLDivElement> | KeyboardEvent<HTMLDivElement>
	) => void
	readonly toggle: () => void
	readonly icon?: IconOptionItems
	readonly showIcon?: boolean
	readonly selected?: string | number
	readonly value?: string | number
	readonly text?: string | number | React.ReactNode
	readonly disabled?: boolean
}

export const OptionItem = ({
	onChange,
	toggle,
	icon,
	showIcon,
	selected,
	value,
	text,
	disabled,
}: OptionItemsType): React.ReactNode => {
	let IconComponent
	if (icon) {
		IconComponent = getIconComponent(icon)
	}

	const handleOptionClick = (event: MouseEvent<HTMLDivElement>): void => {
		if (disabled) {
			return
		}
		onChange(event)
		const { target } = event
		const targetDiv = target as HTMLDivElement
		targetDiv.setAttribute('aria-selected', 'true')
		toggle()
	}

	const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
		const { target } = event
		const targetDiv = target as HTMLDivElement
		if (!disabled && event.key === Keyboard.Enter) {
			onChange(event)
			targetDiv.setAttribute('aria-selected', 'true')
			toggle()
		}
		if (event.key === Keyboard.Escape || event.key === Keyboard.Tab) {
			targetDiv.parentElement?.focus()
			toggle()
		}
	}

	return (
		<div
			role='option'
			aria-selected={value === selected}
			aria-disabled={disabled}
			className={styles.options}
			data-value={value}
			onClick={handleOptionClick}
			onKeyDown={handleKeyDown}
			tabIndex={-1}
		>
			<p
				className={`${styles.value} ${texts.bodyM}`}
				data-value={value}
				data-label={text || value}
			>
				{text || value}
			</p>
			{IconComponent && (
				<span className={styles.icon}>
					{showIcon && <IconComponent color='content-1' />}
				</span>
			)}
		</div>
	)
}
